import React, { useState } from "react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Check, X } from "lucide-react";
import { TYPE_SWATCHES } from "@/lib/entities";
import { toast } from "sonner";

const NEW = "__new_type__";

// A type dropdown that lets the user create their own type inline.
export default function TypeSelect({ value, onChange, types, onAddType, ownerContext = false, testid }) {
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [color, setColor] = useState(TYPE_SWATCHES[6]);

  const reset = () => { setCreating(false); setLabel(""); setColor(TYPE_SWATCHES[6]); };

  const create = async () => {
    const name = label.trim();
    if (!name) return;
    const key = await onAddType({ label: name, color, is_owner: ownerContext });
    if (key) { onChange(key); toast.success(`"${name}" type added`); }
    reset();
  };

  return (
    <div className="space-y-2">
      <Select value={value} onValueChange={(v) => { if (v === NEW) setCreating(true); else onChange(v); }}>
        <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-popover">
          {types.map((t) => (<SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>))}
          <SelectItem value={NEW} data-testid="create-new-type-option" className="text-primary font-medium">
            <span className="flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Create new type…</span>
          </SelectItem>
        </SelectContent>
      </Select>

      {creating && (
        <div className="rounded-lg border border-border bg-secondary/30 p-2.5 space-y-2.5" data-testid="inline-type-create">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }}
            placeholder={ownerContext ? "e.g. Boat, Drone, Squad" : "e.g. Spell, Creature, Safehouse"}
            data-testid="inline-type-name" className="h-9" />
          <div className="flex flex-wrap gap-1">
            {TYPE_SWATCHES.map((c) => (
              <button key={c} type="button" onClick={() => setColor(c)} data-testid={`inline-type-color-${c}`}
                className={`w-5 h-5 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-1 ring-offset-popover ring-foreground scale-110" : ""}`}
                style={{ background: c }} />
            ))}
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={create} disabled={!label.trim()} data-testid="inline-type-add" className="gap-1.5 flex-1">
              <Check className="w-3.5 h-3.5" /> Add type
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={reset} data-testid="inline-type-cancel">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
